import type { Connection, Connector, ErrorType, Evaluate } from '@particle-network/connector-core';
import type { Config } from '../createConfig';
import { filterConnectors } from './filterConnectors';

export type ReconnectParameters = {
  /** Connectors to attempt reconnect with */
  connectors?: readonly Connector[] | undefined;
};

export type ReconnectReturnType = Evaluate<Connection>[];

export type ReconnectErrorType = ErrorType;

let reconnectingConfigUid;

export async function reconnect(config: Config, parameters: ReconnectParameters = {}): Promise<ReconnectReturnType> {
  // If already reconnecting, do nothing
  if (reconnectingConfigUid && reconnectingConfigUid === config.uid) return [];
  const status = reconnectingConfigUid ? 'reconnecting' : undefined;
  reconnectingConfigUid = config.uid;
  config.setState((x) => ({
    ...x,
    status: x.current || status ? 'reconnecting' : 'connecting',
  }));

  const connectors: Connector[] = [];
  if (parameters.connectors?.length) {
    connectors.push(...parameters.connectors);
  } else {
    // 过滤重复的connector(保留EIP6963自动注入的provider)
    const configConnectors = config.connectors;
    connectors.push(...filterConnectors(configConnectors));
  }

  // Try recently-used connectors first
  let recentConnectorId: string | null | undefined;
  try {
    recentConnectorId = await config.storage?.getItem('recentConnectorId');
  } catch {
    // ignore
  }
  const scores: Record<string, number> = {};
  for (const [, connection] of config.state.connections) {
    scores[connection.connector.id] = 1;
  }
  if (recentConnectorId) scores[recentConnectorId] = 0;
  const sorted =
    Object.keys(scores).length > 0
      ? // .toSorted()
        [...connectors].sort((a, b) => (scores[a.id] ?? 10) - (scores[b.id] ?? 10))
      : connectors;

  // Iterate through each connector and try to connect
  let connected = false;
  const connections: Connection[] = [];
  const providers: unknown[] = [];

  for (const connector of sorted) {
    const provider_ = await connector.getProvider().catch(() => undefined);
    if (!provider_) continue;

    // If we already have an instance of this connector's provider,
    // then we have already checked it (ie. injected connectors can
    // share the same `window.ethereum` instance, so we don't want to
    // connect to it again).
    if (providers.some((provider) => provider === provider_)) continue;

    const isAuthorized = await connector.isAuthorized();
    if (!isAuthorized) continue;

    console.log(`reconnect [${connector.id}] start`);
    const data = await connector.connect({ isReconnecting: true }).catch(() => null);
    if (!data) continue;

    connector.emitter.off('connect', config._internal.events.connect);
    connector.emitter.on('change', config._internal.events.change);
    connector.emitter.on('disconnect', config._internal.events.disconnect);

    console.log(`reconnect [${connector.id}] completed`, data);
    config.setState((x) => {
      const connections = new Map(connected ? x.connections : new Map()).set(connector.uid, {
        accounts: data.accounts,
        chainId: data.chainId,
        connector,
      });
      return {
        ...x,
        current: connected ? x.current : connector.uid,
        connections,
      };
    });
    connections.push({
      accounts: data.accounts as readonly [string, ...string[]],
      chainId: data.chainId,
      connector,
    });
    providers.push(provider_);
    connected = true;
  }

  // Prevent overwriting connected status from race condition
  if (config.state.status === 'reconnecting' || config.state.status === 'connecting') {
    // If connecting didn't succeed, set to disconnected
    if (!connected) {
      config.setState((x) => ({
        ...x,
        connections: new Map(),
        current: null,
        status: 'disconnected',
      }));
    } else {
      config.setState((x) => ({ ...x, status: 'connected' }));
    }
  }

  reconnectingConfigUid = undefined;
  return connections;
}
